import React, { useState } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { formatDate } from '../../utils/dateUtils';

const formatDays = (value) => formatLeaveDays(value ?? 0);
const formatDayCount = (value) => {
  const numericValue = Number(value || 0);
  return `${formatDays(value)} ${pluralizeDays(numericValue)}`;
};

export const joinFormattedDates = (isoDates) => {
  const labels = (isoDates || [])
    .map((iso) => formatDate(iso))
    .filter((label) => label && label !== 'N/A' && label !== 'Invalid Date');
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

const formattedLastCredit = (plan) => {
  if (!plan || plan.is_part_time || !plan.last_source_year_accrual_date) return '';
  const label = formatDate(plan.last_source_year_accrual_date);
  if (!label || label === 'N/A' || label === 'Invalid Date') return '';
  return label;
};

const formattedStubStart = (plan) => {
  if (!plan?.source_year_stub_start) return '';
  const label = formatDate(plan.source_year_stub_start);
  if (!label || label === 'N/A' || label === 'Invalid Date') return '';
  return label;
};

const stubDays = (plan) => Number(plan?.source_year_dec_stub_days || 0);

export const yearEndRecommendNote = (plan) => {
  const recommended = Number(plan?.recommended_book_days || 0);
  if (recommended <= 0) return '';
  return `Take at least ${formatDays(plan.recommended_book_days)} ${pluralizeDays(recommended)} by 31 December ${plan.source_year}`;
};

export const yearEndExpiryWarning = ({
  remainingLabel,
  sourceYear,
  destinationYear,
  pending = false,
} = {}) => {
  if (!remainingLabel || sourceYear == null || destinationYear == null) return '';
  const subject = pending
    ? `If your pending leave is not approved, the remaining ${remainingLabel}`
    : `The remaining ${remainingLabel}`;
  return (
    `${subject} must be used by 31 December ${sourceYear}. ` +
    `They will not carry into ${destinationYear} and cannot be used for leave dates from 1 January ${destinationYear}`
  );
};

export const yearEndHoldNote = (plan) => {
  const lastCreditDate = formattedLastCredit(plan);
  const stub = stubDays(plan);
  if (!lastCreditDate) return '';
  if (stub > 0) {
    const stubStart = formattedStubStart(plan);
    const period = stubStart
      ? `${stubStart}–31 December ${plan.source_year}`
      : `31 December ${plan.source_year}`;
    return `After ${lastCreditDate} this projected balance also includes ${formatDays(plan.source_year_dec_stub_days)} days earned ${period}`;
  }
  return `After ${lastCreditDate} this projected balance remains unchanged through 31 December ${plan.source_year}`;
};

export const yearEndCalculationFacts = (plan) => {
  if (!plan || plan.is_part_time) return [];
  const facts = [];
  const remainingCredits = joinFormattedDates(plan.remaining_accrual_dates);
  if (remainingCredits) {
    facts.push(`Still to credit this year: ${remainingCredits}`);
  }
  const holdNote = yearEndHoldNote(plan);
  if (holdNote) {
    facts.push(`${holdNote}.`);
  } else if (stubDays(plan) > 0) {
    facts.push(
      `Includes ${formatDays(plan.source_year_dec_stub_days)} days earned through 31 December ${plan.source_year}.`
    );
  }
  const nextAccrualDate = plan.next_accrual_date
    ? formatDate(plan.next_accrual_date)
    : '';
  if (nextAccrualDate && nextAccrualDate !== 'N/A' && nextAccrualDate !== 'Invalid Date') {
    facts.push(
      `Next credit ${nextAccrualDate} counts toward ${plan.destination_year}, not this projected balance.`
    );
  }
  return facts;
};

const AnnualLeaveYearEndBalance = ({ plan, compact = false }) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  if (!plan?.visible) return null;

  const projectedUnusedLabel = formatDayCount(plan.projected_unused_al);
  const forfeiture = Number(plan.projected_forfeiture || 0);
  const carried = Number(plan.carried_forward_days || 0);
  const approved = Number(plan.approved_al_days || 0);
  const futureApproved = Number(plan.future_approved_al_days || 0);
  const availableTodayLabel = formatDayCount(plan.current_available);
  const recommendNote = yearEndRecommendNote(plan);
  const calculationFacts = yearEndCalculationFacts(plan);
  const isPendingDependent = plan.risk_band === 'pending_dependent';
  const carryForwardSourceYear = Number(plan.source_year) - 1;
  const approvedUsage = futureApproved > 0
    ? `${formatDayCount(approved)} taken or already approved for ${plan.source_year}`
    : `${formatDayCount(approved)} taken in ${plan.source_year}`;
  const breakdown = carried > 0
    ? `${formatDayCount(plan.projected_year_end_entitlement)} projected to be earned in ${plan.source_year} + ${formatDayCount(carried)} carried forward from ${carryForwardSourceYear} − ${approvedUsage}`
    : `${formatDayCount(plan.projected_year_end_entitlement)} projected to be earned in ${plan.source_year} − ${approvedUsage}`;
  const capLabel = formatDayCount(plan.carry_forward_cap);
  const capSummary = `${plan.source_year} Annual Leave carry-forward limit: ${capLabel}`;
  const carryForwardOutcome = forfeiture > 0
    ? `Only ${capLabel} can carry into ${plan.destination_year}`
    : `Your projected balance is within this limit and can carry into ${plan.destination_year} in full`;
  const expiryWarning = forfeiture > 0
    ? yearEndExpiryWarning({
        remainingLabel: formatDayCount(forfeiture),
        sourceYear: plan.source_year,
        destinationYear: plan.destination_year,
        pending: isPendingDependent,
      })
    : '';

  if (compact) {
    if (plan.risk_band === 'safe') return null;

    const pendingDays = Number(plan.pending_al_days || 0);
    const summary = isPendingDependent
      ? `${formatDays(pendingDays)} ${pluralizeDays(pendingDays)} pending · approval would bring you within the ${plan.source_year} Annual Leave carry-forward limit of ${capLabel}; otherwise, excess must be used by 31 Dec`
      : `Only ${capLabel} can carry into ${plan.destination_year}; ${formatDayCount(forfeiture)} must be used by 31 Dec · take at least ${formatDays(plan.recommended_book_days)} ${pluralizeDays(plan.recommended_book_days)} by 31 Dec`;

    return (
      <div
        data-testid="al-year-end-balance"
        className="rounded-lg bg-amber-50 px-3 py-2 flex items-start gap-2 text-amber-800"
      >
        <AlertTriangle
          className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="text-xs font-medium">
            {isPendingDependent ? 'Year-end approval pending' : 'Year-end action needed'}
          </p>
          <p className="text-xs mt-0.5">{summary}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="al-year-end-balance"
      className="rounded-xl border border-blue-200 bg-blue-50 p-3"
    >
      <p className="text-xs font-medium text-blue-700">
        Projected unused Annual Leave as at 31 December {plan.source_year}
      </p>
      <p className="text-lg font-semibold text-blue-900">
        {projectedUnusedLabel}
      </p>
      {plan.current_available != null ? (
        <p className="text-xs text-blue-800 mt-0.5">
          Available today: {availableTodayLabel}
        </p>
      ) : null}
      <p className="text-xs text-blue-800 mt-1">
        <span className="font-medium text-blue-900">Calculation: </span>
        {breakdown}
      </p>
      <div className="mt-1.5">
        <p className="text-xs font-medium text-blue-900">{capSummary}</p>
        <p className="text-xs text-blue-800 mt-0.5">{carryForwardOutcome}.</p>
        {expiryWarning ? (
          <p className="text-xs font-medium text-blue-900 mt-0.5">
            {expiryWarning}.
          </p>
        ) : null}
      </div>
      {recommendNote ? (
        <p className="text-xs font-medium text-blue-900 mt-1">{recommendNote}.</p>
      ) : null}
      {calculationFacts.length > 0 ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setShowBreakdown((open) => !open)}
            aria-expanded={showBreakdown}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-800 hover:text-blue-950 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            How this number is built
            <ChevronDown
              className={`w-3.5 h-3.5 ${showBreakdown ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
          {showBreakdown ? (
            <ul className="mt-1.5 space-y-1 text-xs text-blue-700 list-disc pl-4">
              {calculationFacts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AnnualLeaveYearEndBalance;
